import { useIsJobillan } from '@/hooks'
import { logger } from '@/ui'
import { JobillanBadge } from '@/ui/core/jobillan-badge'
import { useCampaignsShow } from '@jobilla/nexus-sdk'

type Props = {
	campaignId?: string
}

export function OccupationDebugPanel({ campaignId }: Props) {
	const isJobillan = useIsJobillan()
	const { data: campaign, isLoading, error } = useCampaignsShow(campaignId ?? '', {
		enabled: !!campaignId && !!isJobillan,
	})

	if (!isJobillan || !campaignId) {
		return null
	}

	if (error) {
		logger.error(error, { tags: { component: 'OccupationDebugPanel' }, data: { campaignId } })
		return null
	}

	if (isLoading || !campaign) {
		return null
	}

	const classification = campaign.occupationClassification ?? null
	const selectQuestions = (campaign.form?.questions ?? []).filter((q) => q.type === 'select')

	return (
		<div className="rounded-xl bg-gradient-to-br from-slate-300 to-slate-400 p-px shadow-sm ring-1 ring-slate-400/40 max-w-2xl">
			<div className="rounded-xl bg-white p-5 text-md">
				<div className="mb-3 flex items-center justify-between">
					<h3 className="font-semibold uppercase tracking-wide text-slate-700">
						Occupation classification (Jobilla debug)
					</h3>
					<JobillanBadge />
				</div>

				{classification
					? (
						<div>
							<div className="grid grid-cols-2 gap-x-4 gap-y-1 font-mono text-slate-700">
								<div>
									ISCO L2 <span className="text-slate-400">{classification.isco_2_code ?? '—'}</span>
								</div>
								<div className="text-slate-600">{classification.isco_2_label ?? '—'}</div>

								<div>
									ISCO L4 <span className="text-slate-400">{classification.isco_4_code ?? '—'}</span>
								</div>
								<div className="text-slate-600">{classification.isco_4_label ?? '—'}</div>

								<div>
									ESCO L5 <span className="text-slate-400">{classification.esco_5_code ?? '—'}</span>
								</div>
								<div className="text-slate-600">{classification.esco_5_label ?? '—'}</div>

								<div>
									ESCO L6 <span className="text-slate-400">{classification.esco_6_code ?? '—'}</span>
								</div>
								<div className="text-slate-600">{classification.esco_6_label ?? '—'}</div>
							</div>
							{classification.reasoning && (
								<p className="mt-2 text-[11px] italic text-slate-400">{classification.reasoning}</p>
							)}
						</div>
					)
					: <p className="text-slate-500">No occupation_classification on this campaign yet.</p>}

				{selectQuestions.length > 0 && (
					<div className="mt-5">
						<h4 className="mb-2 font-semibold uppercase tracking-wide text-slate-700">
							Form question skill mappings
						</h4>
						<div className="space-y-3">
							{selectQuestions.map((question) => {
								const options = question.options ?? []
								const reason = options.find((o) => o.escoMappingReason)?.escoMappingReason ?? null
								return (
									<div key={question.id} className="rounded border border-slate-200 p-3">
										<p className="truncate text-slate-800" title={question.label}>
											{question.label}
										</p>
										{reason && <p className="mt-1 text-sm italic text-slate-400">{reason}</p>}
										<ul className="mt-1 space-y-1 font-mono text-sm">
											{options.map((option) => {
												const skills = option.escoSkills ?? []
												return (
													<li key={option.id} className="space-y-0.5">
														<span className="text-slate-600">{option.label}</span>
														{skills.length > 0
															? (
																<ul className="ml-3 space-y-0.5">
																	{skills.map((skill) => (
																		<li
																			key={skill.conceptUri}
																			className="text-emerald-700"
																		>
																			↳ {skill.preferredLabel}
																		</li>
																	))}
																</ul>
															)
															: <p className="ml-3 text-slate-400">—</p>}
													</li>
												)
											})}
										</ul>
									</div>
								)
							})}
						</div>
					</div>
				)}
			</div>
		</div>
	)
}
